import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';

interface StickerSitesCountBadgeProps {
  onClick?: () => void;
}

function StickerSitesCountBadge({ onClick }: StickerSitesCountBadgeProps) {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function fetchSitesCount() {
      try {
        setLoading(true);
        setError(false);

        // 軽量なcount取得（head:trueでデータ本体は取得しない）
        const { count: sitesCount, error: countError } = await supabase
          .from('sites')
          .select('id', { count: 'exact', head: true });

        if (countError) {
          console.error('教材サイト件数の取得に失敗しました:', JSON.stringify(countError));
          setError(true);
          return;
        }

        setCount(sitesCount || 0);
      } catch (err) {
        console.error('教材サイト件数の取得に失敗しました:', JSON.stringify(err));
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    fetchSitesCount();
  }, []);

  const label = '教材サイト';
  const suffix = '件掲載中';

  // 表示文字（安全フォールバック）
  const countText =
    loading ? '…' : error || count == null ? null : count.toLocaleString('ja-JP');

  const clickable = typeof onClick === 'function';

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={[
        // ステッカー本体
        'select-none',
        'rounded-2xl',
        'border-2 border-gray-900/10',
        'bg-white/95',
        'shadow-lg',
        'backdrop-blur-sm',
        'px-3 py-2 sm:px-4 sm:py-3',
        'flex items-center gap-2 sm:gap-3',

        // "ステッカー感"
        '-rotate-2',
        'transition-transform duration-200',
        'hover:-rotate-1',
        'active:rotate-0',

        // クリック可能なら
        clickable ? 'cursor-pointer hover:shadow-xl' : '',

        // レスポンシブ対応
        'max-w-[180px] sm:max-w-none',
      ].join(' ')}
      aria-label="教材サイト掲載数"
      style={{
        minHeight: '60px',
      }}
    >
      {/* アイコン */}
      <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-br from-teal-100 to-blue-100 flex items-center justify-center flex-shrink-0">
        <i className="ri-global-line text-lg sm:text-xl text-teal-700" aria-hidden="true"></i>
      </div>

      <div className="leading-tight flex-1 min-w-0">
        <div className="text-[10px] sm:text-xs font-semibold text-gray-700">{label}</div>

        <div className="flex items-end gap-1">
          {countText ? (
            <div className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
              {countText}
            </div>
          ) : (
            <div className="text-sm font-bold text-gray-900">
              {loading ? '…' : '掲載中'}
            </div>
          )}

          <div className="text-xs sm:text-sm font-bold text-gray-800 pb-0.5">{suffix}</div>
        </div>
      </div>
    </div>
  );
}

export default StickerSitesCountBadge;
